import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Brain, Zap, DollarSign, TrendingUp, AlertTriangle, 
  Calendar, RefreshCw, Loader2, BarChart3, Building2,
  Bot, Crown, ArrowUpRight, ArrowDownRight
} from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  BarChart,
  Bar,
  Cell,
  PieChart,
  Pie,
  Legend,
} from "recharts";

const MODULE_COLORS = {
  whatsapp: "#25D366",
  executive: "#6366F1",
  brain: "#EC4899",
  jarvis: "#F59E0B",
  content: "#10B981",
};

const MODULE_LABELS = {
  whatsapp: "WhatsApp AI",
  executive: "Assistente",
  brain: "Brain BI",
  jarvis: "Jarvis",
  content: "Conteudo",
};

function AdminStatCard({ icon: Icon, label, value, subvalue, color = "emerald", trend }) {
  const colorMap = {
    emerald: "text-emerald-600 bg-emerald-500/10",
    blue: "text-blue-600 bg-blue-500/10",
    amber: "text-amber-600 bg-amber-500/10",
    red: "text-red-600 bg-red-500/10",
    purple: "text-purple-600 bg-purple-500/10",
    indigo: "text-indigo-600 bg-indigo-500/10",
  };
  
  return (
    <Card className="bg-gray-900 border-gray-800">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className={`p-2.5 rounded-xl ${colorMap[color]}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <p className="text-xs text-gray-400">{label}</p>
            <p className="text-2xl font-bold text-white">{value}</p>
            {subvalue && <p className="text-xs text-gray-500">{subvalue}</p>}
            {trend !== undefined && (
              <div className={`flex items-center gap-1 text-xs ${trend >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                {trend >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                {Math.abs(trend).toFixed(1)}% vs mes anterior
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function TenantRankingTable({ tenants, title, icon: Icon, alertMode = false }) {
  return (
    <Card className="bg-gray-900 border-gray-800">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-gray-300 flex items-center gap-2">
          <Icon className="w-4 h-4" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <ScrollArea className="h-64">
          <div className="px-4 pb-4 space-y-2">
            {tenants?.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">Nenhum dado</p>
            ) : (
              tenants?.map((t, i) => (
                <div 
                  key={t.restaurant_id || i} 
                  className={`flex items-center gap-3 p-2.5 rounded-lg ${
                    alertMode && t.usage_percent >= 90 
                      ? "bg-red-500/10 border border-red-500/20" 
                      : "bg-gray-800/50"
                  }`}
                >
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    i === 0 ? "bg-amber-500 text-black" :
                    i === 1 ? "bg-gray-400 text-black" :
                    i === 2 ? "bg-amber-700 text-white" :
                    "bg-gray-700 text-gray-300"
                  }`}>
                    {i + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">{t.restaurant_name}</p>
                    <p className="text-xs text-gray-400">
                      {t.plan || "trial"} - {t.calls?.toLocaleString()} chamadas
                    </p>
                  </div>
                  <div className="text-right">
                    {alertMode ? (
                      <Badge className={`${
                        t.usage_percent >= 100 ? "bg-red-500" :
                        t.usage_percent >= 90 ? "bg-orange-500" :
                        t.usage_percent >= 70 ? "bg-amber-500" :
                        "bg-emerald-500"
                      } text-white text-xs`}>
                        {t.usage_percent?.toFixed(0)}%
                      </Badge>
                    ) : (
                      <span className="text-sm font-bold text-emerald-400">
                        ${t.cost_usd?.toFixed(2)}
                      </span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}

function ModuleBreakdownChart({ data }) {
  const chartData = Object.entries(data || {}).map(([key, val]) => ({
    name: MODULE_LABELS[key] || key,
    calls: val.calls || 0,
    cost: val.cost || 0,
    color: MODULE_COLORS[key] || "#6B7280",
  }));
  
  const totalCalls = chartData.reduce((sum, d) => sum + d.calls, 0);
  
  return (
    <Card className="bg-gray-900 border-gray-800">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-gray-300 flex items-center gap-2">
          <BarChart3 className="w-4 h-4" />
          Breakdown por Modulo
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-4">
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={70}
                  paddingAngle={2}
                  dataKey="calls"
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value) => [value.toLocaleString(), "Chamadas"]}
                  contentStyle={{ 
                    backgroundColor: "#1F2937", 
                    border: "1px solid #374151",
                    borderRadius: "8px",
                    fontSize: 11 
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-2 py-2">
            {chartData.map((item) => (
              <div key={item.name} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                  <span className="text-xs text-gray-400">{item.name}</span>
                </div>
                <div className="text-right">
                  <span className="text-xs font-medium text-white">{((item.calls / totalCalls) * 100).toFixed(1)}%</span>
                  <span className="text-xs text-gray-500 ml-2">${item.cost.toFixed(2)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ModelBreakdownChart({ data }) {
  const chartData = Object.entries(data || {}).map(([key, val]) => ({
    name: key.length > 15 ? key.slice(0, 15) + "..." : key,
    fullName: key,
    calls: val.calls || 0,
    cost: val.cost || 0,
  })).sort((a, b) => b.calls - a.calls).slice(0, 6);
  
  return (
    <Card className="bg-gray-900 border-gray-800">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-gray-300 flex items-center gap-2">
          <Bot className="w-4 h-4" />
          Breakdown por Modelo
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} layout="vertical">
              <XAxis type="number" tick={{ fill: "#9CA3AF", fontSize: 10 }} />
              <YAxis type="category" dataKey="name" tick={{ fill: "#9CA3AF", fontSize: 10 }} width={100} />
              <Tooltip 
                formatter={(value, name, props) => [
                  name === "cost" ? `$${value.toFixed(4)}` : value.toLocaleString(),
                  name === "cost" ? "Custo" : "Chamadas"
                ]}
                labelFormatter={(label, payload) => payload?.[0]?.payload?.fullName || label}
                contentStyle={{ 
                  backgroundColor: "#1F2937", 
                  border: "1px solid #374151",
                  borderRadius: "8px",
                  fontSize: 11 
                }}
              />
              <Bar dataKey="calls" fill="#6366F1" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

function DailyTrendChart({ data }) {
  const chartData = (data || []).slice(-14).map(d => ({
    date: d.date?.slice(5) || "",
    calls: d.calls || 0,
    cost: d.cost || 0,
  }));
  
  return (
    <Card className="bg-gray-900 border-gray-800 col-span-2">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-gray-300 flex items-center gap-2">
          <Calendar className="w-4 h-4" />
          Tendencia Diaria (ultimos 14 dias)
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="colorCallsAdmin" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#6366F1" stopOpacity={0.4}/>
                  <stop offset="95%" stopColor="#6366F1" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorCostAdmin" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10B981" stopOpacity={0.4}/>
                  <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <XAxis dataKey="date" tick={{ fill: "#9CA3AF", fontSize: 10 }} />
              <YAxis yAxisId="left" tick={{ fill: "#9CA3AF", fontSize: 10 }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fill: "#9CA3AF", fontSize: 10 }} />
              <Tooltip 
                formatter={(value, name) => [
                  name === "cost" ? `$${value.toFixed(4)}` : value.toLocaleString(),
                  name === "cost" ? "Custo" : "Chamadas"
                ]}
                contentStyle={{ 
                  backgroundColor: "#1F2937", 
                  border: "1px solid #374151",
                  borderRadius: "8px",
                  fontSize: 11 
                }}
              />
              <Legend 
                formatter={(value) => value === "calls" ? "Chamadas" : "Custo"}
                wrapperStyle={{ fontSize: 11 }}
              />
              <Area 
                yAxisId="left"
                type="monotone" 
                dataKey="calls" 
                stroke="#6366F1" 
                strokeWidth={2}
                fill="url(#colorCallsAdmin)" 
              />
              <Area 
                yAxisId="right"
                type="monotone" 
                dataKey="cost" 
                stroke="#10B981" 
                strokeWidth={2}
                fill="url(#colorCostAdmin)" 
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

export function AIUsageAdminTab({ adminAxios }) {
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  
  const fetchData = useCallback(async () => {
    try {
      const res = await adminAxios.get("/ai/usage/monthly-report");
      setReport(res.data);
    } catch (err) {
      console.error("Error fetching AI usage report:", err);
      toast.error("Erro ao carregar dados de uso da IA");
    }
    setLoading(false);
  }, [adminAxios]);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
    toast.success("Dados atualizados");
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }
  
  const summary = report?.summary || {};
  const byRestaurant = report?.by_restaurant || [];
  
  // Sort tenants by different criteria
  const topByCost = [...byRestaurant].sort((a, b) => (b.cost_usd || 0) - (a.cost_usd || 0)).slice(0, 10);
  const nearLimit = byRestaurant.filter(t => (t.usage_percent || 0) >= 70).sort((a, b) => (b.usage_percent || 0) - (a.usage_percent || 0)).slice(0, 10);
  
  // Aggregate by module and model across all tenants
  const byModule = {};
  const byModel = {};
  byRestaurant.forEach(t => {
    if (t.top_module) {
      byModule[t.top_module] = byModule[t.top_module] || { calls: 0, cost: 0 };
      byModule[t.top_module].calls += t.calls || 0;
      byModule[t.top_module].cost += t.cost_usd || 0;
    }
    if (t.top_model) {
      byModel[t.top_model] = byModel[t.top_model] || { calls: 0, cost: 0 };
      byModel[t.top_model].calls += t.calls || 0;
      byModel[t.top_model].cost += t.cost_usd || 0;
    }
  });
  
  return (
    <div className="space-y-6" data-testid="ai-usage-admin-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Brain className="w-6 h-6 text-indigo-400" />
            Consumo de IA - Plataforma
          </h2>
          <p className="text-sm text-gray-400">
            Mes: {report?.month || new Date().toISOString().slice(0, 7)}
          </p>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={handleRefresh}
          disabled={refreshing}
          className="gap-1.5 border-gray-700 text-gray-300 hover:bg-gray-800"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          Atualizar
        </Button>
      </div>
      
      {/* Platform Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <AdminStatCard 
          icon={Building2} 
          label="Restaurantes Ativos" 
          value={summary.total_restaurants || byRestaurant.length}
          color="blue"
        />
        <AdminStatCard 
          icon={Zap} 
          label="Chamadas Totais" 
          value={(summary.total_calls || 0).toLocaleString()}
          color="purple"
        />
        <AdminStatCard 
          icon={DollarSign} 
          label="Custo Total IA" 
          value={`$${(summary.total_cost_usd || 0).toFixed(2)}`}
          color="emerald"
        />
        <AdminStatCard 
          icon={AlertTriangle} 
          label="Proximos do Limite" 
          value={nearLimit.length}
          subvalue={nearLimit.length > 0 ? "Requer atencao" : "Todos OK"}
          color={nearLimit.length > 0 ? "amber" : "emerald"}
        />
      </div>
      
      {/* Charts Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ModuleBreakdownChart data={byModule} />
        <ModelBreakdownChart data={byModel} />
      </div>
      
      {/* Daily Trend */}
      <div className="grid grid-cols-1 gap-4">
        <DailyTrendChart data={report?.daily_breakdown || []} />
      </div>
      
      {/* Tenant Rankings */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <TenantRankingTable 
          tenants={topByCost} 
          title="Top 10 por Custo" 
          icon={Crown}
        />
        <TenantRankingTable 
          tenants={nearLimit} 
          title="Proximos do Limite" 
          icon={AlertTriangle}
          alertMode
        />
      </div>
    </div>
  );
}

export default AIUsageAdminTab;
